import { Grid } from '../../core/Grid.js';
import {
    Position,
    Edge,
    positionKey,
    parsePositionKey,
    edgeAdjacentCells
} from '../../core/GridGeometry.js';
import { IgnitionGame } from './IgnitionGame.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

type Quarter = 'NW' | 'NE' | 'SW' | 'SE';
const ALL_QUARTERS: Quarter[] = ['NW', 'NE', 'SW', 'SE'];

function quarterKey(row: number, col: number, quarter: Quarter): string {
    return `${row},${col},${quarter}`;
}

export class IgnitionVortex extends IgnitionGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        // Recompute the fire simulation: a Hub gate deletion can prune an active
        // pair here, and checkWin/render read the cached chamber state.
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.recalculate();
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            if (this.getGrid().hasDigitAt(p)) return false;
            if (this.getGrid().hasDigitAt(paired)) return false;
            // No fuses on either gate cell
            if (this.hasFuseInCell(p)) return false;
            if (this.hasFuseInCell(paired)) return false;
            return true;
        });
    }

    private hasFuseInCell(pos: Position): boolean {
        const segments = this.getFuseSegments();
        for (const dir of ['N', 'E', 'S', 'W']) {
            if (segments.has(`${pos.row},${pos.col},${dir}`)) return true;
        }
        return false;
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.recalculate();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Path Through Gates ──────────────────────────────────────────

    protected override getGateQuarterNeighbors(
        row: number, col: number, _quarter: Quarter
    ): Array<{ row: number; col: number; quarter: Quarter }> {
        if (!this.gm.hasActiveGates()) return [];

        const key = positionKey({ row, col });
        const pairedKey = this.gm.raw.get(key);
        if (!pairedKey) return [];

        const paired = parsePositionKey(pairedKey);
        return ALL_QUARTERS.map(q => ({ row: paired.row, col: paired.col, quarter: q }));
    }

    // ── Placement Blocking ────────────────────────────────────────

    override toggleFuse(edge: Edge): boolean {
        // Block fuse placement on active gate cells
        const cells = edgeAdjacentCells(edge);
        for (const cell of cells) {
            if (this.gm.raw.has(positionKey(cell))) return false;
        }
        return super.toggleFuse(edge);
    }

    // ── Chamber Merging ───────────────────────────────────────────

    protected override onChambersComputed(): void {
        if (!this.gm || !this.gm.hasActiveGates()) return;

        // For each active gate pair, merge their chambers
        const processed = new Set<string>();
        for (const [key, pairedKey] of this.gm.raw) {
            const pairId = [key, pairedKey].sort().join('|');
            if (processed.has(pairId)) continue;
            processed.add(pairId);

            const posA = parsePositionKey(key);
            const posB = parsePositionKey(pairedKey);

            // Get chamber IDs from any quarter of each gate cell
            // (gate cells have no fuses/digits, so all 4 quarters are the same chamber)
            const qKeyA = quarterKey(posA.row, posA.col, 'NW');
            const qKeyB = quarterKey(posB.row, posB.col, 'NW');
            const chamberA = this.quarterToChamber.get(qKeyA);
            const chamberB = this.quarterToChamber.get(qKeyB);

            if (chamberA === undefined || chamberB === undefined || chamberA === chamberB) continue;

            // Merge: replace all occurrences of chamberB with chamberA
            for (const [qk, cid] of this.quarterToChamber) {
                if (cid === chamberB) {
                    this.quarterToChamber.set(qk, chamberA);
                }
            }
        }
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        if (this.gm.hasActiveGates()) {
            this.recalculate();
        }
    }
}
